import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { FacialVerificationModal } from '../../components/banking/FacialVerificationModal';
import {
  Send, Star, Clock, Plus, Trash2, Edit2, ArrowUpRight,
  User, Check, Search, ChevronLeft, ChevronRight,
  AlertCircle, RefreshCw, X,
} from 'lucide-react';

type TransferTab = 'transfer' | 'favorites' | 'history';

interface Favorite {
  id: string;
  name: string;
  pixKey: string;
  pixKeyType: string;
  type: string;
  bankName?: string;
  accountType?: string;
  recipientName?: string;
}

interface PaginatedRes<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface HistoryItem {
  id: string;
  type: string;
  amount: string;
  fee: string;
  netAmount: string;
  fromAccountId: string;
  toAccountId: string;
  description: string;
  status: string;
  pixKey?: string;
  createdAt: string;
}

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

function getStatusBadge(status: string) {
  const map: Record<string, { label: string; color: string; bg: string }> = {
    COMPLETED: { label: 'Concluído', color: 'text-green-400', bg: 'bg-green-500/20' },
    PENDING: { label: 'Pendente', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
    FAILED: { label: 'Falhou', color: 'text-red-400', bg: 'bg-red-500/20' },
    PROCESSING: { label: 'Processando', color: 'text-blue-400', bg: 'bg-blue-500/20' },
    CANCELLED: { label: 'Cancelado', color: 'text-gray-400', bg: 'bg-gray-500/20' },
  };
  return map[status] || { label: status, color: 'text-surface-400', bg: 'bg-surface-700' };
}

function PaginationControls({ page, totalPages, onPageChange }: { page: number; totalPages: number; onPageChange: (p: number) => void }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-3 mt-4">
      <button
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        className="p-2 rounded-lg bg-surface-800 text-surface-300 hover:bg-surface-700 disabled:opacity-40 disabled:cursor-not-allowed"
        aria-label="Página anterior"
      >
        <ChevronLeft size={18} />
      </button>
      <span className="text-sm text-surface-400">
        Página {page} de {totalPages}
      </span>
      <button
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
        className="p-2 rounded-lg bg-surface-800 text-surface-300 hover:bg-surface-700 disabled:opacity-40 disabled:cursor-not-allowed"
        aria-label="Próxima página"
      >
        <ChevronRight size={18} />
      </button>
    </div>
  );
}

export function CustomerTransfers() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TransferTab>('transfer');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showFavoriteModal, setShowFavoriteModal] = useState(false);
  const [editingFavorite, setEditingFavorite] = useState<Favorite | null>(null);

  // Facial verification state
  const [showFacialVerification, setShowFacialVerification] = useState(false);
  const [verificationSessionId, setVerificationSessionId] = useState<string | undefined>();
  const [verificationReason, setVerificationReason] = useState<string | undefined>();
  const [pendingTransferData, setPendingTransferData] = useState<any>(null);

  // Transfer form state
  const [selectedFavorite, setSelectedFavorite] = useState<Favorite | null>(null);
  const [pixKey, setPixKey] = useState('');
  const [pixKeyType, setPixKeyType] = useState('CPF');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [saveAsFavorite, setSaveAsFavorite] = useState(false);
  const [favoriteName, setFavoriteName] = useState('');
  const [transferSuccess, setTransferSuccess] = useState<{ amount: string; key: string } | null>(null);

  // Favorite form state
  const [newFavoriteName, setNewFavoriteName] = useState('');
  const [newFavoritePixKey, setNewFavoritePixKey] = useState('');

  // Favorites search & pagination
  const [favSearch, setFavSearch] = useState('');
  const [favPage, setFavPage] = useState(1);
  const debouncedFavSearch = useDebounce(favSearch, 300);

  // History filters & pagination
  const [historySearch, setHistorySearch] = useState('');
  const [historyStartDate, setHistoryStartDate] = useState('');
  const [historyEndDate, setHistoryEndDate] = useState('');
  const [historyStatus, setHistoryStatus] = useState('');
  const [historyPage, setHistoryPage] = useState(1);
  const debouncedHistorySearch = useDebounce(historySearch, 300);

  // Toast state for CRUD errors
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const showToast = useCallback((msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 5000);
  }, []);

  // Reset pages when filters change
  useEffect(() => { setFavPage(1); }, [debouncedFavSearch]);
  useEffect(() => { setHistoryPage(1); }, [debouncedHistorySearch, historyStartDate, historyEndDate, historyStatus]);

  // Fetch favorites (paginated)
  const {
    data: favorites,
    isLoading: favoritesLoading,
    isError: favoritesError,
    refetch: refetchFavorites,
  } = useQuery<PaginatedRes<Favorite>>({
    queryKey: ['transfers', 'favorites', favPage, debouncedFavSearch],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(favPage));
      params.set('limit', '20');
      if (debouncedFavSearch) params.set('search', debouncedFavSearch);
      const response = await api.get(`/banking/transfers/favorites?${params.toString()}`);
      return response.data;
    },
  });

  // Fetch transfer history (paginated + filtered)
  const {
    data: transferHistory,
    isLoading: historyLoading,
    isError: historyError,
    refetch: refetchHistory,
  } = useQuery<PaginatedRes<HistoryItem>>({
    queryKey: ['transfers', 'history', historyPage, debouncedHistorySearch, historyStartDate, historyEndDate, historyStatus],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(historyPage));
      params.set('limit', '20');
      if (debouncedHistorySearch) params.set('search', debouncedHistorySearch);
      if (historyStartDate) params.set('startDate', historyStartDate);
      if (historyEndDate) params.set('endDate', historyEndDate);
      if (historyStatus) params.set('status', historyStatus);
      const response = await api.get(`/banking/transfers/history?${params.toString()}`);
      return response.data;
    },
    enabled: activeTab === 'history',
  });

  const [transferError, setTransferError] = useState<string | null>(null);

  // Transfer mutation
  const transferMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/banking/transfers', data);
      return response.data;
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message || error?.message || 'Erro na transferência';
      if (msg.includes('Saldo insuficiente') || msg.includes('INSUFFICIENT_BALANCE')) {
        setTransferError('Saldo insuficiente para realizar esta transferência.');
      } else if (msg.includes('gateway') || msg.includes('indisponível')) {
        setTransferError('Gateway de pagamentos temporariamente indisponível. Tente novamente.');
      } else if (msg.includes('Conta não está ativa')) {
        setTransferError('Sua conta não está ativa. Entre em contato com o suporte.');
      } else {
        setTransferError(msg);
      }
      setTimeout(() => setTransferError(null), 6000);
    },
    onSuccess: (data) => {
      if (data.requiresVerification) {
        setVerificationSessionId(data.sessionId);
        setVerificationReason(data.reason);
        setPendingTransferData({ pixKey, amount: parseAmount(amount), description });
        setShowConfirmModal(false);
        setShowFacialVerification(true);
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['banking', 'balance'] });
      queryClient.invalidateQueries({ queryKey: ['transfers', 'history'] });
      queryClient.invalidateQueries({ queryKey: ['statement'] });
      queryClient.invalidateQueries({ queryKey: ['banking', 'transactions'] });
      queryClient.invalidateQueries({ queryKey: ['pix', 'history'] });
      if (saveAsFavorite && favoriteName) {
        createFavoriteMutation.mutate({ name: favoriteName, pixKey });
      }
      const sentAmount = amount;
      const sentKey = pixKey;
      setShowConfirmModal(false);
      resetForm();
      setTransferSuccess({ amount: sentAmount, key: sentKey });
      setTimeout(() => setTransferSuccess(null), 4000);
    },
  });

  // Create favorite mutation
  const createFavoriteMutation = useMutation({
    mutationFn: async (data: { name: string; pixKey: string }) => {
      const response = await api.post('/banking/transfers/favorites', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transfers', 'favorites'] });
      setShowFavoriteModal(false);
      setNewFavoriteName('');
      setNewFavoritePixKey('');
    },
    onError: (error: any) => {
      showToast(error?.response?.data?.message || 'Erro ao criar favorito');
    },
  });

  // Update favorite mutation
  const updateFavoriteMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await api.put(`/banking/transfers/favorites/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transfers', 'favorites'] });
      setShowFavoriteModal(false);
      setEditingFavorite(null);
    },
    onError: (error: any) => {
      showToast(error?.response?.data?.message || 'Erro ao atualizar favorito');
    },
  });

  // Delete favorite mutation
  const deleteFavoriteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/banking/transfers/favorites/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transfers', 'favorites'] });
    },
    onError: (error: any) => {
      showToast(error?.response?.data?.message || 'Erro ao remover favorito');
    },
  });

  const resetForm = () => {
    setSelectedFavorite(null);
    setPixKey('');
    setPixKeyType('CPF');
    setAmount('');
    setDescription('');
    setSaveAsFavorite(false);
    setFavoriteName('');
    setPendingTransferData(null);
    setVerificationSessionId(undefined);
    setVerificationReason(undefined);
  };

  const handleFacialVerificationComplete = async (sessionId: string) => {
    setShowFacialVerification(false);
    if (pendingTransferData) {
      transferMutation.mutate({ ...pendingTransferData, verificationSessionId: sessionId });
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value / 100);
  };

  const formatDate = (date: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    }).format(new Date(date));
  };

  const parseAmount = (value: string): number => {
    const cleaned = value.replace(/\D/g, '');
    return parseInt(cleaned, 10) || 0;
  };

  const handleAmountChange = (value: string) => {
    const numericValue = value.replace(/\D/g, '');
    const cents = parseInt(numericValue, 10) || 0;
    const formatted = (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    setAmount(formatted);
  };

  const handleSelectFavorite = (favorite: Favorite) => {
    setSelectedFavorite(favorite);
    setPixKey(favorite.pixKey);
  };

  const handleTransfer = () => {
    const amountCents = parseAmount(amount);
    transferMutation.mutate({ pixKey, pixKeyType, amount: amountCents, description });
  };

  const handleSaveFavorite = () => {
    if (editingFavorite) {
      updateFavoriteMutation.mutate({ id: editingFavorite.id, data: { name: newFavoriteName, pixKey: newFavoritePixKey } });
    } else {
      createFavoriteMutation.mutate({ name: newFavoriteName, pixKey: newFavoritePixKey });
    }
  };

  const openEditFavorite = (favorite: Favorite) => {
    setEditingFavorite(favorite);
    setNewFavoriteName(favorite.name);
    setNewFavoritePixKey(favorite.pixKey);
    setShowFavoriteModal(true);
  };

  const tabs = [
    { id: 'transfer' as TransferTab, label: 'Transferir', icon: <Send size={18} /> },
    { id: 'favorites' as TransferTab, label: 'Favoritos', icon: <Star size={18} /> },
    { id: 'history' as TransferTab, label: 'Histórico', icon: <Clock size={18} /> },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Transferências</h1>
        <p className="text-surface-400">Transfira para outras contas</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'bg-primary-500 text-white'
                : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Transfer Tab */}
      {activeTab === 'transfer' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Favorites Quick Select */}
          <Card className="lg:col-span-1">
            <div className="p-4">
              <h3 className="text-sm font-medium text-surface-300 mb-3">Favoritos</h3>
              {favoritesLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="p-3 rounded-lg bg-surface-800/50 animate-pulse">
                      <div className="h-4 w-24 bg-surface-700 rounded" />
                    </div>
                  ))}
                </div>
              ) : favorites?.items?.length ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {favorites.items.slice(0, 5).map((favorite: Favorite) => (
                    <button
                      key={favorite.id}
                      onClick={() => handleSelectFavorite(favorite)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                        selectedFavorite?.id === favorite.id
                          ? 'bg-primary-500/20 border border-primary-500'
                          : 'bg-surface-800/50 hover:bg-surface-800'
                      }`}
                    >
                      <div className="w-8 h-8 rounded-full bg-surface-700 flex items-center justify-center">
                        <User size={16} className="text-surface-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{favorite.name}</p>
                        <p className="text-xs text-surface-400 truncate">{favorite.pixKey}</p>
                      </div>
                      {selectedFavorite?.id === favorite.id && (
                        <Check size={16} className="text-primary-400" />
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <Star size={24} className="mx-auto text-surface-600 mb-2" />
                  <p className="text-xs text-surface-400">Sem favoritos</p>
                </div>
              )}
            </div>
          </Card>

          {/* Transfer Form */}
          <Card className="lg:col-span-2">
            <div className="p-6 space-y-6">
              <h2 className="text-lg font-semibold text-white">Nova Transferência</h2>
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2">Tipo de chave</label>
                <div className="flex flex-wrap gap-2">
                  {(['CPF', 'CNPJ', 'EMAIL', 'PHONE', 'RANDOM'] as const).map((type) => (
                    <button key={type} onClick={() => setPixKeyType(type)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${pixKeyType === type ? 'bg-primary-500 text-white' : 'bg-surface-800 text-surface-300 hover:bg-surface-700'}`}>
                      {type === 'RANDOM' ? 'Aleatória' : type === 'PHONE' ? 'Telefone' : type}
                    </button>
                  ))}
                </div>
              </div>
              <Input
                label="Chave Pix do destinatário"
                placeholder="CPF, CNPJ, e-mail, telefone ou chave aleatória"
                value={pixKey}
                onChange={(e) => { setPixKey(e.target.value); setSelectedFavorite(null); }}
              />
              <Input
                label="Valor"
                placeholder="0,00"
                value={amount}
                onChange={(e) => handleAmountChange(e.target.value)}
                leftIcon={<span className="text-surface-400">R$</span>}
              />
              <Input
                label="Descrição (opcional)"
                placeholder="Ex: Pagamento de aluguel"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="saveAsFavorite"
                  checked={saveAsFavorite}
                  onChange={(e) => setSaveAsFavorite(e.target.checked)}
                  className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500"
                />
                <label htmlFor="saveAsFavorite" className="text-sm text-surface-300">Salvar como favorito</label>
              </div>
              {saveAsFavorite && (
                <Input label="Nome do favorito" placeholder="Ex: João - Aluguel" value={favoriteName} onChange={(e) => setFavoriteName(e.target.value)} />
              )}
              <Button variant="primary" className="w-full" onClick={() => setShowConfirmModal(true)} disabled={!pixKey || !amount || transferMutation.isPending}>
                <Send size={20} className="mr-2" />
                Transferir
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Favorites Tab */}
      {activeTab === 'favorites' && (
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Meus Favoritos</h2>
              <Button variant="primary" size="sm" onClick={() => { setEditingFavorite(null); setNewFavoriteName(''); setNewFavoritePixKey(''); setShowFavoriteModal(true); }}>
                <Plus size={16} className="mr-2" />
                Novo Favorito
              </Button>
            </div>

            {/* Search */}
            <div className="mb-4">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
                <input
                  type="text"
                  placeholder="Buscar favorito por nome ou chave..."
                  value={favSearch}
                  onChange={(e) => setFavSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-surface-800 border border-surface-700 text-white placeholder-surface-400 text-sm focus:outline-none focus:border-primary-500"
                />
              </div>
            </div>

            {favoritesError ? (
              <div className="text-center py-8">
                <AlertCircle size={48} className="mx-auto text-red-400 mb-3" />
                <p className="text-surface-400 mb-3">Erro ao carregar favoritos</p>
                <Button variant="secondary" size="sm" onClick={() => refetchFavorites()}>
                  <RefreshCw size={16} className="mr-2" />
                  Tentar novamente
                </Button>
              </div>
            ) : favoritesLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-4 p-4 rounded-lg bg-surface-800/50 animate-pulse">
                    <div className="w-12 h-12 rounded-full bg-surface-700" />
                    <div className="flex-1">
                      <div className="h-4 w-32 bg-surface-700 rounded mb-2" />
                      <div className="h-3 w-48 bg-surface-700 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : favorites?.items?.length ? (
              <>
                <div className="text-xs text-surface-500 mb-2">{favorites.total} favorito(s) encontrado(s)</div>
                <div className="space-y-3">
                  {favorites.items.map((favorite: Favorite) => (
                    <div key={favorite.id} className="flex items-center gap-4 p-4 rounded-lg bg-surface-800/50">
                      <div className="w-12 h-12 rounded-full bg-primary-500/20 flex items-center justify-center">
                        <User size={24} className="text-primary-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white">{favorite.name}</p>
                        <p className="text-xs text-surface-400 truncate">{favorite.pixKey}</p>
                        {favorite.bankName && <p className="text-xs text-surface-500">{favorite.bankName}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => { handleSelectFavorite(favorite); setActiveTab('transfer'); }}>
                          <Send size={16} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEditFavorite(favorite)}>
                          <Edit2 size={16} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteFavoriteMutation.mutate(favorite.id)} disabled={deleteFavoriteMutation.isPending}>
                          <Trash2 size={16} className="text-red-400" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <PaginationControls page={favorites.page} totalPages={favorites.totalPages} onPageChange={setFavPage} />
              </>
            ) : (
              <div className="text-center py-8">
                <Star size={48} className="mx-auto text-surface-600 mb-3" />
                <p className="text-surface-400">{debouncedFavSearch ? 'Nenhum favorito encontrado' : 'Nenhum favorito cadastrado'}</p>
                {!debouncedFavSearch && (
                  <Button variant="primary" size="sm" className="mt-4" onClick={() => setShowFavoriteModal(true)}>
                    Adicionar favorito
                  </Button>
                )}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Histórico de Transferências</h2>

            {/* Filters */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <div>
                <label className="block text-xs text-surface-400 mb-1">Data início</label>
                <input
                  type="date"
                  value={historyStartDate}
                  onChange={(e) => setHistoryStartDate(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-surface-800 border border-surface-700 text-white text-sm focus:outline-none focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs text-surface-400 mb-1">Data fim</label>
                <input
                  type="date"
                  value={historyEndDate}
                  onChange={(e) => setHistoryEndDate(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-surface-800 border border-surface-700 text-white text-sm focus:outline-none focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs text-surface-400 mb-1">Status</label>
                <select
                  value={historyStatus}
                  onChange={(e) => setHistoryStatus(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-surface-800 border border-surface-700 text-white text-sm focus:outline-none focus:border-primary-500"
                >
                  <option value="">Todos</option>
                  <option value="COMPLETED">Concluído</option>
                  <option value="PENDING">Pendente</option>
                  <option value="FAILED">Falhou</option>
                  <option value="PROCESSING">Processando</option>
                  <option value="CANCELLED">Cancelado</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-surface-400 mb-1">Buscar</label>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
                  <input
                    type="text"
                    placeholder="Descrição ou chave..."
                    value={historySearch}
                    onChange={(e) => setHistorySearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 rounded-lg bg-surface-800 border border-surface-700 text-white placeholder-surface-400 text-sm focus:outline-none focus:border-primary-500"
                  />
                </div>
              </div>
            </div>

            {historyError ? (
              <div className="text-center py-8">
                <AlertCircle size={48} className="mx-auto text-red-400 mb-3" />
                <p className="text-surface-400 mb-3">Erro ao carregar histórico</p>
                <Button variant="secondary" size="sm" onClick={() => refetchHistory()}>
                  <RefreshCw size={16} className="mr-2" />
                  Tentar novamente
                </Button>
              </div>
            ) : historyLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-surface-800/50 animate-pulse">
                    <div className="w-10 h-10 rounded-full bg-surface-700" />
                    <div className="flex-1">
                      <div className="h-4 w-32 bg-surface-700 rounded mb-2" />
                      <div className="h-3 w-24 bg-surface-700 rounded" />
                    </div>
                    <div className="h-5 w-20 bg-surface-700 rounded" />
                  </div>
                ))}
              </div>
            ) : transferHistory?.items?.length ? (
              <>
                <div className="text-xs text-surface-500 mb-2">{transferHistory.total} transferência(s) encontrada(s)</div>
                <div className="space-y-2">
                  {transferHistory.items.map((transfer: HistoryItem) => {
                    const badge = getStatusBadge(transfer.status);
                    return (
                      <div key={transfer.id} className="flex items-center gap-4 p-3 rounded-lg bg-surface-800/50 hover:bg-surface-800 transition-colors">
                        <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                          <ArrowUpRight size={20} className="text-red-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate">
                            {transfer.description || transfer.pixKey || 'Transferência'}
                          </p>
                          <p className="text-xs text-surface-400">{formatDate(transfer.createdAt)}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${badge.bg} ${badge.color}`}>
                          {badge.label}
                        </span>
                        <span className="text-sm font-semibold text-red-400">
                          -{formatCurrency(Number(transfer.amount))}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <PaginationControls page={transferHistory.page} totalPages={transferHistory.totalPages} onPageChange={setHistoryPage} />
              </>
            ) : (
              <div className="text-center py-8">
                <Send size={48} className="mx-auto text-surface-600 mb-3" />
                <p className="text-surface-400">Nenhuma transferência encontrada</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Confirm Transfer Modal */}
      <Modal isOpen={showConfirmModal} onClose={() => setShowConfirmModal(false)} title="Confirmar Transferência">
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-surface-800">
            <div className="flex justify-between mb-2">
              <span className="text-surface-400">Destinatário</span>
              <span className="text-white">{selectedFavorite?.name || pixKey}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="text-surface-400">Chave Pix</span>
              <span className="text-white text-sm truncate max-w-[200px]">{pixKey}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-surface-400">Valor</span>
              <span className="text-white font-semibold">R$ {amount}</span>
            </div>
            {description && (
              <div className="flex justify-between mt-2">
                <span className="text-surface-400">Descrição</span>
                <span className="text-white">{description}</span>
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setShowConfirmModal(false)}>Cancelar</Button>
            <Button variant="primary" className="flex-1" onClick={handleTransfer} disabled={transferMutation.isPending}>
              {transferMutation.isPending ? 'Processando...' : 'Confirmar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Favorite Modal */}
      <Modal isOpen={showFavoriteModal} onClose={() => { setShowFavoriteModal(false); setEditingFavorite(null); }} title={editingFavorite ? 'Editar Favorito' : 'Novo Favorito'}>
        <div className="space-y-4">
          <Input label="Nome" placeholder="Ex: João Silva" value={newFavoriteName} onChange={(e) => setNewFavoriteName(e.target.value)} />
          <Input label="Chave Pix" placeholder="CPF, CNPJ, e-mail, telefone ou chave aleatória" value={newFavoritePixKey} onChange={(e) => setNewFavoritePixKey(e.target.value)} />
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => { setShowFavoriteModal(false); setEditingFavorite(null); }}>Cancelar</Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={handleSaveFavorite}
              disabled={!newFavoriteName || !newFavoritePixKey || createFavoriteMutation.isPending || updateFavoriteMutation.isPending}
            >
              {createFavoriteMutation.isPending || updateFavoriteMutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Facial Verification Modal */}
      <FacialVerificationModal
        isOpen={showFacialVerification}
        onClose={() => { setShowFacialVerification(false); setPendingTransferData(null); }}
        onVerified={handleFacialVerificationComplete}
        operationType="TRANSFER"
        operationValue={pendingTransferData?.amount}
        sessionId={verificationSessionId}
        reason={verificationReason}
      />

      {/* Success Notification */}
      {transferSuccess && (
        <div className="fixed top-4 right-4 z-50">
          <div className="bg-green-500/90 backdrop-blur-sm text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3 max-w-sm">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <Check size={24} />
            </div>
            <div>
              <p className="font-semibold">Transferência realizada!</p>
              <p className="text-sm text-green-100">R$ {transferSuccess.amount} para {transferSuccess.key}</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Notification */}
      {transferError && (
        <div className="fixed top-4 right-4 z-50">
          <div className="bg-red-500/90 backdrop-blur-sm text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3 max-w-sm">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <AlertCircle size={24} />
            </div>
            <div>
              <p className="font-semibold">Erro na transferência</p>
              <p className="text-sm text-red-100">{transferError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Toast for CRUD errors */}
      {toastMessage && (
        <div className="fixed top-4 right-4 z-50">
          <div className="bg-red-500/90 backdrop-blur-sm text-white px-5 py-3 rounded-xl shadow-2xl flex items-center gap-3 max-w-sm">
            <AlertCircle size={20} />
            <span className="text-sm flex-1">{toastMessage}</span>
            <button onClick={() => setToastMessage(null)} className="hover:bg-white/10 rounded p-1">
              <X size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerTransfers;
